// IlmHub Curriculum Architecture — JSON-driven scalable lesson system

export type DevelopmentalLevel = 'infant' | 'toddler' | 'preschool' | 'kindergarten' | 'early_childhood' | 'foundation' | 'elementary' | 'middle_school' | 'high_school' | 'college_prep' | 'undergraduate' | 'graduate' | 'continuing_ed';

export type CareerTrack = 'medicine' | 'engineering' | 'ai_tech' | 'entrepreneurship' | 'law' | 'islamic_scholarship' | 'education' | 'global_dev' | 'finance' | 'arts_design' | 'healthcare' | 'trades_vocational' | 'data_science' | 'cybersecurity' | 'public_health' | 'agriculture' | 'logistics';

export interface LearningObjective {
  id: string;
  description: string;
  bloomsLevel: 'remember' | 'understand' | 'apply' | 'analyze' | 'evaluate' | 'create';
  standard?: string;
}

export interface Lesson {
  id: string;
  title: string;
  titleAr?: string;
  subjectId: string;
  level: DevelopmentalLevel;
  careerRelevance: CareerTrack[];
  duration: number;
  xpReward: number;
  objectives: LearningObjective[];
  activities: string[];
  quiz: QuizQuestion[];
  gameId?: string;
  standards: string[];
  accessibilityNotes: string[];
  teacherGuide: string;
  projectTask?: string;
  adabConnection?: string;
  languages: string[];
  videoType?: 'animated' | 'whiteboard' | 'ai_narrator' | 'interactive' | 'cartoon';
  worksheetUrl?: string;
  offlineAvailable?: boolean;
}

export interface QuizQuestion {
  id: string;
  question: string;
  options: string[];
  correct: number;
  explanation: string;
}

export interface Subject {
  id: string;
  name: string;
  nameAr?: string;
  emoji: string;
  color: string;
  gradient: string;
  description: string;
  levels: DevelopmentalLevel[];
  careerConnections: CareerTrack[];
  totalLessons: number;
  gameCount: number;
  category: SubjectCategory;
  featured?: boolean;
}

export type SubjectCategory = 'core_academic' | 'stem' | 'islamic_arabic' | 'medical_health' | 'trades_vocational' | 'professional' | 'languages' | 'arts_creative' | 'social_sciences';

export interface CareerPathway {
  id: CareerTrack;
  label: string;
  emoji: string;
  coreSubjects: string[];
  supportSubjects: string[];
  description: string;
  levelProgression: Record<DevelopmentalLevel, string[]>;
  certifications?: string[];
  salaryRange?: string;
}

export interface CurriculumStandard {
  id: string;
  name: string;
  country: string;
  region: string;
  grades: string;
  subjects: string[];
}

export const CURRICULUM_STANDARDS: CurriculumStandard[] = [
  { id: 'common_core', name: 'Common Core State Standards', country: 'USA', region: 'Americas', grades: 'K-12', subjects: ['mathematics', 'english_literacy'] },
  { id: 'canadian', name: 'Canadian Provincial Curriculum', country: 'Canada', region: 'Americas', grades: 'K-12', subjects: ['mathematics', 'science', 'english_literacy', 'social_studies'] },
  { id: 'uk_national', name: 'UK National Curriculum', country: 'United Kingdom', region: 'Europe', grades: 'KS1-KS5', subjects: ['mathematics', 'science', 'english_literacy'] },
  { id: 'cambridge', name: 'Cambridge International Curriculum', country: 'International', region: 'Global', grades: 'Primary-A Level', subjects: ['mathematics', 'science', 'english_literacy', 'social_studies'] },
  { id: 'ib', name: 'International Baccalaureate', country: 'International', region: 'Global', grades: 'PYP-DP', subjects: ['mathematics', 'science', 'english_literacy', 'social_studies', 'languages'] },
  { id: 'waec', name: 'WAEC West African Curriculum', country: 'West Africa', region: 'Africa', grades: 'SS1-SS3', subjects: ['mathematics', 'science', 'english_literacy', 'social_studies'] },
  { id: 'neco', name: 'NECO Nigerian Curriculum', country: 'Nigeria', region: 'Africa', grades: 'JSS-SSS', subjects: ['mathematics', 'science', 'english_literacy', 'social_studies'] },
  { id: 'nigerian', name: 'Nigerian National Curriculum', country: 'Nigeria', region: 'Africa', grades: 'Nursery-SS3', subjects: ['mathematics', 'science', 'english_literacy', 'social_studies', 'islamic_studies'] },
  { id: 'uae', name: 'UAE Ministry of Education', country: 'UAE', region: 'Middle East', grades: 'KG-Grade 12', subjects: ['mathematics', 'science', 'english_literacy', 'arabic', 'islamic_studies'] },
  { id: 'saudi', name: 'Saudi Ministry of Education', country: 'Saudi Arabia', region: 'Middle East', grades: 'KG-Grade 12', subjects: ['mathematics', 'science', 'arabic', 'islamic_studies'] },
  { id: 'south_african', name: 'South African CAPS', country: 'South Africa', region: 'Africa', grades: 'Grade R-12', subjects: ['mathematics', 'science', 'english_literacy', 'social_studies'] },
  { id: 'australian', name: 'Australian Curriculum (ACARA)', country: 'Australia', region: 'Oceania', grades: 'F-10', subjects: ['mathematics', 'science', 'english_literacy', 'social_studies', 'technology_ai'] },
];

export const DEVELOPMENTAL_LEVELS: Record<DevelopmentalLevel, { label: string; ageRange: string; emoji: string; color: string }> = {
  infant: { label: 'Infant', ageRange: '0–12 months', emoji: '👶', color: 'bg-rose-100 text-rose-700 border-rose-200' },
  toddler: { label: 'Toddler', ageRange: '1–3 years', emoji: '🧸', color: 'bg-orange-100 text-orange-700 border-orange-200' },
  preschool: { label: 'Preschool', ageRange: '3–4 years', emoji: '🎠', color: 'bg-yellow-100 text-yellow-700 border-yellow-200' },
  kindergarten: { label: 'Kindergarten', ageRange: '4–6 years', emoji: '🌈', color: 'bg-lime-100 text-lime-700 border-lime-200' },
  early_childhood: { label: 'Early Childhood', ageRange: '0–5 years', emoji: '🍼', color: 'bg-pink-100 text-pink-700 border-pink-200' },
  foundation: { label: 'Foundation', ageRange: '5–7 years', emoji: '🌱', color: 'bg-green-100 text-green-700 border-green-200' },
  elementary: { label: 'Elementary', ageRange: '7–11 years', emoji: '📚', color: 'bg-blue-100 text-blue-700 border-blue-200' },
  middle_school: { label: 'Middle School', ageRange: '11–14 years', emoji: '🔬', color: 'bg-purple-100 text-purple-700 border-purple-200' },
  high_school: { label: 'High School', ageRange: '14–18 years', emoji: '🎓', color: 'bg-amber-100 text-amber-700 border-amber-200' },
  college_prep: { label: 'College Prep', ageRange: '17–19 years', emoji: '🏛️', color: 'bg-teal-100 text-teal-700 border-teal-200' },
  undergraduate: { label: 'Undergraduate', ageRange: '18–22 years', emoji: '🔭', color: 'bg-indigo-100 text-indigo-700 border-indigo-200' },
  graduate: { label: 'Graduate / Professional', ageRange: '22+ years', emoji: '🏆', color: 'bg-rose-100 text-rose-700 border-rose-200' },
  continuing_ed: { label: 'Continuing Education', ageRange: 'All ages', emoji: '♾️', color: 'bg-slate-100 text-slate-700 border-slate-200' },
};

export const SUBJECTS: Subject[] = [
  // CORE ACADEMIC
  {
    id: 'mathematics',
    name: 'Mathematics',
    nameAr: 'الرياضيات',
    emoji: '🔢',
    color: 'text-blue-700',
    gradient: 'from-blue-500 to-indigo-600',
    description: 'Number sense, algebra, geometry, calculus, statistics, and financial literacy from counting to advanced research mathematics.',
    levels: ['infant', 'toddler', 'preschool', 'kindergarten', 'early_childhood', 'foundation', 'elementary', 'middle_school', 'high_school', 'college_prep', 'undergraduate', 'graduate'],
    careerConnections: ['medicine', 'engineering', 'ai_tech', 'finance', 'education', 'data_science'],
    totalLessons: 480,
    gameCount: 24,
    category: 'core_academic',
    featured: true,
  },
  {
    id: 'english_literacy',
    name: 'English & Literacy',
    nameAr: 'اللغة الإنجليزية والقراءة',
    emoji: '📖',
    color: 'text-emerald-700',
    gradient: 'from-emerald-500 to-teal-600',
    description: 'Phonics, reading comprehension, writing, grammar, rhetoric, academic writing, and advanced literary analysis.',
    levels: ['infant', 'toddler', 'preschool', 'kindergarten', 'early_childhood', 'foundation', 'elementary', 'middle_school', 'high_school', 'college_prep', 'undergraduate', 'graduate'],
    careerConnections: ['law', 'education', 'global_dev', 'arts_design', 'entrepreneurship'],
    totalLessons: 420,
    gameCount: 20,
    category: 'core_academic',
    featured: true,
  },
  {
    id: 'science',
    name: 'Science',
    nameAr: 'العلوم',
    emoji: '🔬',
    color: 'text-violet-700',
    gradient: 'from-violet-500 to-purple-600',
    description: 'Life science, earth science, physics, chemistry, biology, ecology, research methodology, and advanced scientific inquiry.',
    levels: ['early_childhood', 'foundation', 'elementary', 'middle_school', 'high_school', 'college_prep', 'undergraduate', 'graduate'],
    careerConnections: ['medicine', 'engineering', 'ai_tech', 'education', 'global_dev', 'public_health'],
    totalLessons: 460,
    gameCount: 22,
    category: 'core_academic',
    featured: true,
  },
  {
    id: 'social_studies',
    name: 'Social Studies',
    nameAr: 'الدراسات الاجتماعية',
    emoji: '🌍',
    color: 'text-amber-700',
    gradient: 'from-amber-500 to-orange-600',
    description: 'Geography, history, civics, economics, global citizenship, cultural studies, political science, and international relations.',
    levels: ['early_childhood', 'foundation', 'elementary', 'middle_school', 'high_school', 'college_prep', 'undergraduate', 'graduate'],
    careerConnections: ['law', 'global_dev', 'education', 'entrepreneurship', 'islamic_scholarship'],
    totalLessons: 380,
    gameCount: 18,
    category: 'social_sciences',
  },
  // STEM
  {
    id: 'physics',
    name: 'Physics',
    nameAr: 'الفيزياء',
    emoji: '⚛️',
    color: 'text-blue-700',
    gradient: 'from-blue-600 to-cyan-600',
    description: 'Mechanics, thermodynamics, electromagnetism, optics, quantum physics, and modern physics applications.',
    levels: ['middle_school', 'high_school', 'college_prep', 'undergraduate', 'graduate'],
    careerConnections: ['engineering', 'medicine', 'ai_tech', 'education'],
    totalLessons: 280,
    gameCount: 14,
    category: 'stem',
  },
  {
    id: 'chemistry',
    name: 'Chemistry',
    nameAr: 'الكيمياء',
    emoji: '⚗️',
    color: 'text-orange-700',
    gradient: 'from-orange-500 to-red-600',
    description: 'Atomic structure, periodic table, chemical bonding, reactions, organic chemistry, and biochemistry.',
    levels: ['middle_school', 'high_school', 'college_prep', 'undergraduate', 'graduate'],
    careerConnections: ['medicine', 'engineering', 'public_health', 'agriculture'],
    totalLessons: 260,
    gameCount: 12,
    category: 'stem',
  },
  {
    id: 'biology',
    name: 'Biology',
    nameAr: 'الأحياء',
    emoji: '🧬',
    color: 'text-green-700',
    gradient: 'from-green-500 to-emerald-600',
    description: 'Cell biology, genetics, evolution, ecology, human anatomy, physiology, and biotechnology.',
    levels: ['elementary', 'middle_school', 'high_school', 'college_prep', 'undergraduate', 'graduate'],
    careerConnections: ['medicine', 'public_health', 'agriculture', 'education'],
    totalLessons: 300,
    gameCount: 16,
    category: 'stem',
    featured: true,
  },
  {
    id: 'computer_science',
    name: 'Computer Science',
    nameAr: 'علوم الحاسوب',
    emoji: '💻',
    color: 'text-slate-700',
    gradient: 'from-slate-600 to-gray-700',
    description: 'Programming fundamentals, algorithms, data structures, software engineering, and computer systems.',
    levels: ['foundation', 'elementary', 'middle_school', 'high_school', 'college_prep', 'undergraduate', 'graduate'],
    careerConnections: ['ai_tech', 'engineering', 'finance', 'education', 'data_science', 'cybersecurity'],
    totalLessons: 340,
    gameCount: 20,
    category: 'stem',
    featured: true,
  },
  {
    id: 'technology_ai',
    name: 'Technology & AI',
    nameAr: 'التكنولوجيا والذكاء الاصطناعي',
    emoji: '🤖',
    color: 'text-sky-700',
    gradient: 'from-sky-500 to-blue-600',
    description: 'Digital literacy, coding, computational thinking, AI ethics, data science, cybersecurity, and emerging technologies.',
    levels: ['early_childhood', 'foundation', 'elementary', 'middle_school', 'high_school', 'college_prep', 'undergraduate', 'graduate'],
    careerConnections: ['ai_tech', 'engineering', 'medicine', 'finance', 'education', 'data_science', 'cybersecurity'],
    totalLessons: 360,
    gameCount: 18,
    category: 'stem',
    featured: true,
  },
  {
    id: 'robotics',
    name: 'Robotics & Engineering',
    nameAr: 'الروبوتيكس والهندسة',
    emoji: '🦾',
    color: 'text-cyan-700',
    gradient: 'from-cyan-500 to-blue-600',
    description: 'Robotics design, programming, mechanical engineering, electronics, and automation systems.',
    levels: ['elementary', 'middle_school', 'high_school', 'college_prep', 'undergraduate', 'graduate'],
    careerConnections: ['engineering', 'ai_tech', 'education'],
    totalLessons: 200,
    gameCount: 12,
    category: 'stem',
  },
  {
    id: 'data_science',
    name: 'Data Science & Analytics',
    nameAr: 'علم البيانات والتحليل',
    emoji: '📊',
    color: 'text-violet-700',
    gradient: 'from-violet-500 to-indigo-600',
    description: 'Statistics, data visualization, machine learning, Python, R, and real-world data analysis projects.',
    levels: ['high_school', 'college_prep', 'undergraduate', 'graduate', 'continuing_ed'],
    careerConnections: ['data_science', 'ai_tech', 'finance', 'public_health', 'medicine'],
    totalLessons: 220,
    gameCount: 10,
    category: 'stem',
  },
  {
    id: 'cybersecurity',
    name: 'Cybersecurity',
    nameAr: 'الأمن السيبراني',
    emoji: '🔐',
    color: 'text-red-700',
    gradient: 'from-red-500 to-rose-600',
    description: 'Network security, ethical hacking, cryptography, digital forensics, and security operations.',
    levels: ['high_school', 'college_prep', 'undergraduate', 'graduate', 'continuing_ed'],
    careerConnections: ['cybersecurity', 'ai_tech', 'engineering'],
    totalLessons: 180,
    gameCount: 8,
    category: 'stem',
  },
  // ISLAMIC & ARABIC
  {
    id: 'quran',
    name: 'Quran & Tajweed',
    nameAr: 'القرآن والتجويد',
    emoji: '📿',
    color: 'text-emerald-700',
    gradient: 'from-emerald-600 to-teal-700',
    description: 'Quran recitation, Tajweed rules, Hifdh memorization, and understanding the meanings of the Holy Quran.',
    levels: ['infant', 'toddler', 'preschool', 'kindergarten', 'early_childhood', 'foundation', 'elementary', 'middle_school', 'high_school', 'college_prep', 'undergraduate', 'graduate', 'continuing_ed'],
    careerConnections: ['islamic_scholarship', 'education'],
    totalLessons: 500,
    gameCount: 24,
    category: 'islamic_arabic',
    featured: true,
  },
  {
    id: 'arabic',
    name: 'Arabic Language',
    nameAr: 'اللغة العربية',
    emoji: '🔤',
    color: 'text-teal-700',
    gradient: 'from-teal-500 to-cyan-600',
    description: 'Arabic alphabet, grammar (Nahw & Sarf), Quranic Arabic, Modern Standard Arabic, and conversational Arabic.',
    levels: ['infant', 'toddler', 'preschool', 'kindergarten', 'early_childhood', 'foundation', 'elementary', 'middle_school', 'high_school', 'college_prep', 'undergraduate', 'graduate'],
    careerConnections: ['islamic_scholarship', 'education', 'global_dev', 'law'],
    totalLessons: 440,
    gameCount: 22,
    category: 'islamic_arabic',
    featured: true,
  },
  {
    id: 'islamic_studies',
    name: 'Islamic Studies & Adab',
    nameAr: 'الدراسات الإسلامية والأدب',
    emoji: '🕌',
    color: 'text-teal-700',
    gradient: 'from-teal-500 to-cyan-600',
    description: 'Aqidah, Seerah, Fiqh foundations, Hadith studies, Islamic civilization, ethics, adab, and character development.',
    levels: ['infant', 'toddler', 'preschool', 'kindergarten', 'early_childhood', 'foundation', 'elementary', 'middle_school', 'high_school', 'college_prep', 'undergraduate', 'graduate'],
    careerConnections: ['islamic_scholarship', 'education', 'global_dev', 'law', 'medicine'],
    totalLessons: 400,
    gameCount: 20,
    category: 'islamic_arabic',
    featured: true,
  },
  {
    id: 'hadith',
    name: 'Hadith Sciences',
    nameAr: 'علوم الحديث',
    emoji: '📜',
    color: 'text-amber-700',
    gradient: 'from-amber-500 to-yellow-600',
    description: 'Hadith terminology, chain of narration, classification, and the six major Hadith collections.',
    levels: ['middle_school', 'high_school', 'college_prep', 'undergraduate', 'graduate'],
    careerConnections: ['islamic_scholarship', 'education', 'law'],
    totalLessons: 200,
    gameCount: 8,
    category: 'islamic_arabic',
  },
  {
    id: 'fiqh',
    name: 'Fiqh & Islamic Law',
    nameAr: 'الفقه والشريعة الإسلامية',
    emoji: '⚖️',
    color: 'text-indigo-700',
    gradient: 'from-indigo-500 to-violet-600',
    description: 'Islamic jurisprudence, worship, transactions, family law, and contemporary Fiqh issues.',
    levels: ['elementary', 'middle_school', 'high_school', 'college_prep', 'undergraduate', 'graduate'],
    careerConnections: ['islamic_scholarship', 'law', 'finance', 'education'],
    totalLessons: 240,
    gameCount: 10,
    category: 'islamic_arabic',
  },
  {
    id: 'islamic_finance',
    name: 'Islamic Finance',
    nameAr: 'التمويل الإسلامي',
    emoji: '💰',
    color: 'text-green-700',
    gradient: 'from-green-600 to-emerald-700',
    description: 'Halal banking, Zakat, Waqf, Sukuk, Islamic economics, and ethical investment principles.',
    levels: ['high_school', 'college_prep', 'undergraduate', 'graduate', 'continuing_ed'],
    careerConnections: ['finance', 'islamic_scholarship', 'entrepreneurship', 'law'],
    totalLessons: 160,
    gameCount: 6,
    category: 'islamic_arabic',
  },
  // MEDICAL & HEALTHCARE
  {
    id: 'nursing',
    name: 'Nursing & Allied Health',
    nameAr: 'التمريض والصحة المساعدة',
    emoji: '🏥',
    color: 'text-blue-700',
    gradient: 'from-blue-500 to-cyan-600',
    description: 'Nursing fundamentals, patient care, clinical skills, pharmacology, NCLEX preparation, and healthcare ethics.',
    levels: ['high_school', 'college_prep', 'undergraduate', 'graduate', 'continuing_ed'],
    careerConnections: ['healthcare', 'medicine', 'public_health'],
    totalLessons: 320,
    gameCount: 14,
    category: 'medical_health',
    featured: true,
  },
  {
    id: 'public_health',
    name: 'Public Health & Epidemiology',
    nameAr: 'الصحة العامة والوبائيات',
    emoji: '🌡️',
    color: 'text-rose-700',
    gradient: 'from-rose-500 to-pink-600',
    description: 'Epidemiology, disease prevention, global health, health policy, nutrition, and community health programs.',
    levels: ['high_school', 'college_prep', 'undergraduate', 'graduate', 'continuing_ed'],
    careerConnections: ['public_health', 'medicine', 'global_dev', 'education'],
    totalLessons: 240,
    gameCount: 10,
    category: 'medical_health',
  },
  {
    id: 'pharmacy_tech',
    name: 'Pharmacy Technology',
    nameAr: 'تقنية الصيدلة',
    emoji: '💊',
    color: 'text-purple-700',
    gradient: 'from-purple-500 to-violet-600',
    description: 'Pharmacology basics, drug dispensing, medication safety, pharmacy law, and clinical pharmacy practice.',
    levels: ['high_school', 'college_prep', 'undergraduate', 'continuing_ed'],
    careerConnections: ['healthcare', 'medicine', 'public_health'],
    totalLessons: 180,
    gameCount: 8,
    category: 'medical_health',
  },
  {
    id: 'nutrition',
    name: 'Nutrition & Dietetics',
    nameAr: 'التغذية والحمية',
    emoji: '🥗',
    color: 'text-lime-700',
    gradient: 'from-lime-500 to-green-600',
    description: 'Macronutrients, micronutrients, therapeutic nutrition, Islamic dietary guidelines, and clinical dietetics.',
    levels: ['elementary', 'middle_school', 'high_school', 'college_prep', 'undergraduate', 'graduate', 'continuing_ed'],
    careerConnections: ['healthcare', 'public_health', 'medicine', 'education'],
    totalLessons: 200,
    gameCount: 10,
    category: 'medical_health',
  },
  {
    id: 'health_wellness',
    name: 'Health & Wellness',
    nameAr: 'الصحة والعافية',
    emoji: '💚',
    color: 'text-green-700',
    gradient: 'from-green-500 to-emerald-600',
    description: 'Physical health, mental wellness, anatomy, public health, medical ethics, and holistic wellbeing.',
    levels: ['early_childhood', 'foundation', 'elementary', 'middle_school', 'high_school', 'college_prep', 'undergraduate', 'graduate'],
    careerConnections: ['medicine', 'education', 'global_dev', 'islamic_scholarship', 'public_health'],
    totalLessons: 300,
    gameCount: 14,
    category: 'medical_health',
  },
  // TRADES & VOCATIONAL
  {
    id: 'carpentry',
    name: 'Carpentry & Woodworking',
    nameAr: 'النجارة وأعمال الخشب',
    emoji: '🪚',
    color: 'text-amber-700',
    gradient: 'from-amber-600 to-orange-700',
    description: 'Wood joinery, furniture making, construction carpentry, safety, and blueprint reading.',
    levels: ['middle_school', 'high_school', 'college_prep', 'continuing_ed'],
    careerConnections: ['trades_vocational', 'engineering'],
    totalLessons: 120,
    gameCount: 6,
    category: 'trades_vocational',
  },
  {
    id: 'electrical',
    name: 'Electrical Technology',
    nameAr: 'التقنية الكهربائية',
    emoji: '⚡',
    color: 'text-yellow-700',
    gradient: 'from-yellow-500 to-amber-600',
    description: 'Electrical circuits, wiring, safety codes, renewable energy, and electrical installation.',
    levels: ['middle_school', 'high_school', 'college_prep', 'continuing_ed'],
    careerConnections: ['trades_vocational', 'engineering'],
    totalLessons: 140,
    gameCount: 6,
    category: 'trades_vocational',
  },
  {
    id: 'culinary_arts',
    name: 'Culinary Arts & Food Science',
    nameAr: 'فنون الطهي وعلوم الغذاء',
    emoji: '👨‍🍳',
    color: 'text-orange-700',
    gradient: 'from-orange-500 to-red-600',
    description: 'Cooking techniques, food science, halal food preparation, nutrition, and culinary entrepreneurship.',
    levels: ['middle_school', 'high_school', 'college_prep', 'continuing_ed'],
    careerConnections: ['trades_vocational', 'entrepreneurship', 'public_health'],
    totalLessons: 160,
    gameCount: 8,
    category: 'trades_vocational',
  },
  {
    id: 'fashion_design',
    name: 'Fashion & Textile Design',
    nameAr: 'تصميم الأزياء والنسيج',
    emoji: '👗',
    color: 'text-pink-700',
    gradient: 'from-pink-500 to-rose-600',
    description: 'Fashion design, pattern making, textile science, modest fashion, and fashion entrepreneurship.',
    levels: ['middle_school', 'high_school', 'college_prep', 'continuing_ed'],
    careerConnections: ['trades_vocational', 'arts_design', 'entrepreneurship'],
    totalLessons: 140,
    gameCount: 6,
    category: 'trades_vocational',
  },
  {
    id: 'agriculture',
    name: 'Agriculture & Food Systems',
    nameAr: 'الزراعة وأنظمة الغذاء',
    emoji: '🌾',
    color: 'text-green-700',
    gradient: 'from-green-600 to-lime-600',
    description: 'Crop science, sustainable farming, hydroponics, food security, and agricultural entrepreneurship.',
    levels: ['elementary', 'middle_school', 'high_school', 'college_prep', 'undergraduate', 'continuing_ed'],
    careerConnections: ['agriculture', 'public_health', 'global_dev', 'entrepreneurship'],
    totalLessons: 180,
    gameCount: 8,
    category: 'trades_vocational',
  },
  // PROFESSIONAL
  {
    id: 'economics_entrepreneurship',
    name: 'Economics & Entrepreneurship',
    nameAr: 'الاقتصاد وريادة الأعمال',
    emoji: '💼',
    color: 'text-yellow-700',
    gradient: 'from-yellow-500 to-amber-600',
    description: 'Personal finance, microeconomics, macroeconomics, business planning, Islamic finance, startup ecosystems, and global markets.',
    levels: ['foundation', 'elementary', 'middle_school', 'high_school', 'college_prep', 'undergraduate', 'graduate'],
    careerConnections: ['entrepreneurship', 'finance', 'global_dev', 'law', 'education'],
    totalLessons: 320,
    gameCount: 16,
    category: 'professional',
    featured: true,
  },
  {
    id: 'accounting_finance',
    name: 'Accounting & Finance',
    nameAr: 'المحاسبة والمالية',
    emoji: '🧾',
    color: 'text-emerald-700',
    gradient: 'from-emerald-600 to-green-700',
    description: 'Financial accounting, managerial accounting, auditing, taxation, and financial analysis.',
    levels: ['high_school', 'college_prep', 'undergraduate', 'graduate', 'continuing_ed'],
    careerConnections: ['finance', 'entrepreneurship', 'law'],
    totalLessons: 240,
    gameCount: 10,
    category: 'professional',
  },
  {
    id: 'logistics_supply_chain',
    name: 'Logistics & Supply Chain',
    nameAr: 'اللوجستيات وسلسلة التوريد',
    emoji: '🚚',
    color: 'text-blue-700',
    gradient: 'from-blue-600 to-indigo-700',
    description: 'Supply chain management, logistics operations, inventory control, global trade, and transportation systems.',
    levels: ['high_school', 'college_prep', 'undergraduate', 'graduate', 'continuing_ed'],
    careerConnections: ['logistics', 'entrepreneurship', 'global_dev'],
    totalLessons: 160,
    gameCount: 6,
    category: 'professional',
  },
  {
    id: 'academic_writing',
    name: 'Academic Writing & Research',
    nameAr: 'الكتابة الأكاديمية والبحث',
    emoji: '✍️',
    color: 'text-slate-700',
    gradient: 'from-slate-500 to-gray-600',
    description: 'Research methodology, academic writing, citation, critical thinking, and scholarly publication.',
    levels: ['high_school', 'college_prep', 'undergraduate', 'graduate'],
    careerConnections: ['education', 'law', 'medicine', 'islamic_scholarship', 'global_dev'],
    totalLessons: 180,
    gameCount: 6,
    category: 'professional',
  },
  // LANGUAGES
  {
    id: 'languages',
    name: 'World Languages',
    nameAr: 'اللغات العالمية',
    emoji: '🗣️',
    color: 'text-indigo-700',
    gradient: 'from-indigo-500 to-violet-600',
    description: 'French, Spanish, Hausa, Yoruba, Swahili, Urdu, Portuguese, and multilingual communication skills.',
    levels: ['early_childhood', 'foundation', 'elementary', 'middle_school', 'high_school', 'college_prep', 'undergraduate', 'graduate'],
    careerConnections: ['islamic_scholarship', 'global_dev', 'education', 'law', 'arts_design'],
    totalLessons: 440,
    gameCount: 22,
    category: 'languages',
    featured: true,
  },
  // ARTS & CREATIVE
  {
    id: 'art_design',
    name: 'Art & Design',
    nameAr: 'الفن والتصميم',
    emoji: '🎨',
    color: 'text-pink-700',
    gradient: 'from-pink-500 to-rose-600',
    description: 'Visual arts, Islamic geometric art, calligraphy, digital design, architecture, media production, and creative expression.',
    levels: ['early_childhood', 'foundation', 'elementary', 'middle_school', 'high_school', 'college_prep', 'undergraduate', 'graduate'],
    careerConnections: ['arts_design', 'engineering', 'education', 'entrepreneurship'],
    totalLessons: 280,
    gameCount: 14,
    category: 'arts_creative',
  },
  {
    id: 'graphic_design',
    name: 'Graphic Design & UI/UX',
    nameAr: 'التصميم الجرافيكي وتجربة المستخدم',
    emoji: '🖌️',
    color: 'text-purple-700',
    gradient: 'from-purple-500 to-pink-600',
    description: 'Visual design principles, typography, branding, UI/UX design, Figma, and digital media production.',
    levels: ['middle_school', 'high_school', 'college_prep', 'undergraduate', 'continuing_ed'],
    careerConnections: ['arts_design', 'ai_tech', 'entrepreneurship'],
    totalLessons: 200,
    gameCount: 10,
    category: 'arts_creative',
  },
  {
    id: 'video_production',
    name: 'Video Production & Media',
    nameAr: 'إنتاج الفيديو والإعلام',
    emoji: '🎬',
    color: 'text-red-700',
    gradient: 'from-red-500 to-orange-600',
    description: 'Video editing, cinematography, storytelling, documentary production, and digital media creation.',
    levels: ['middle_school', 'high_school', 'college_prep', 'undergraduate', 'continuing_ed'],
    careerConnections: ['arts_design', 'education', 'entrepreneurship'],
    totalLessons: 160,
    gameCount: 8,
    category: 'arts_creative',
  },
];

export const SUBJECT_CATEGORIES: Record<SubjectCategory, { label: string; emoji: string; color: string; description: string }> = {
  core_academic: { label: 'Core Academic', emoji: '📚', color: 'bg-blue-100 text-blue-700', description: 'Mathematics, English, Science, Social Studies' },
  stem: { label: 'STEM', emoji: '🔬', color: 'bg-purple-100 text-purple-700', description: 'Physics, Chemistry, Biology, Computer Science, AI, Robotics' },
  islamic_arabic: { label: 'Islamic & Arabic', emoji: '🕌', color: 'bg-emerald-100 text-emerald-700', description: 'Quran, Tajweed, Arabic, Fiqh, Hadith, Islamic Finance' },
  medical_health: { label: 'Medical & Health', emoji: '🏥', color: 'bg-rose-100 text-rose-700', description: 'Nursing, Public Health, Pharmacy, Nutrition' },
  trades_vocational: { label: 'Trades & Vocational', emoji: '🔧', color: 'bg-amber-100 text-amber-700', description: 'Carpentry, Electrical, Culinary, Fashion, Agriculture' },
  professional: { label: 'Professional', emoji: '💼', color: 'bg-teal-100 text-teal-700', description: 'Business, Finance, Logistics, Academic Writing' },
  languages: { label: 'Languages', emoji: '🗣️', color: 'bg-indigo-100 text-indigo-700', description: 'Arabic, French, Spanish, Hausa, Yoruba, Swahili, Urdu' },
  arts_creative: { label: 'Arts & Creative', emoji: '🎨', color: 'bg-pink-100 text-pink-700', description: 'Art, Graphic Design, Video Production, Calligraphy' },
  social_sciences: { label: 'Social Sciences', emoji: '🌍', color: 'bg-orange-100 text-orange-700', description: 'History, Geography, Civics, Economics, Political Science' },
};

export const CAREER_PATHWAYS: CareerPathway[] = [
  {
    id: 'medicine',
    label: 'Medicine & Health Sciences',
    emoji: '⚕️',
    coreSubjects: ['science', 'mathematics', 'health_wellness', 'technology_ai', 'biology', 'chemistry'],
    supportSubjects: ['english_literacy', 'social_studies', 'islamic_studies', 'public_health'],
    description: 'From biology basics to biomedical research, clinical practice, and medical ethics.',
    certifications: ['NCLEX-RN', 'USMLE', 'CPR/BLS', 'CNA', 'Medical Assistant'],
    salaryRange: '$60,000–$250,000+',
    levelProgression: {
      infant: ['Sensory development', 'Body awareness'],
      toddler: ['Body parts', 'Healthy habits'],
      preschool: ['Feelings & emotions', 'Healthy foods'],
      kindergarten: ['Human body basics', 'Hygiene'],
      early_childhood: ['Body awareness', 'Healthy habits', 'Senses exploration'],
      foundation: ['Human body basics', 'Nutrition', 'Hygiene & health'],
      elementary: ['Body systems', 'Plants & animals', 'Health science'],
      middle_school: ['Cell biology', 'Human anatomy', 'Chemistry basics', 'Health research'],
      high_school: ['Biology', 'Chemistry', 'Physics', 'Pre-med foundations', 'Medical ethics'],
      college_prep: ['AP Biology', 'AP Chemistry', 'Statistics', 'Research methods'],
      undergraduate: ['Biochemistry', 'Physiology', 'Pathology', 'Clinical skills'],
      graduate: ['Medical research', 'Specialization', 'Clinical practice', 'Ethics in medicine'],
      continuing_ed: ['CME credits', 'Specialization updates', 'Clinical research'],
    },
  },
  {
    id: 'engineering',
    label: 'Engineering & Architecture',
    emoji: '⚙️',
    coreSubjects: ['mathematics', 'technology_ai', 'science', 'art_design', 'physics', 'computer_science'],
    supportSubjects: ['economics_entrepreneurship', 'social_studies'],
    description: 'Design thinking, systems engineering, architecture, and innovation from building blocks to advanced engineering.',
    certifications: ['PE License', 'PMP', 'AutoCAD Certified', 'LEED'],
    salaryRange: '$70,000–$180,000+',
    levelProgression: {
      infant: ['Cause and effect', 'Stacking'],
      toddler: ['Building blocks', 'Simple structures'],
      preschool: ['Shapes & structures', 'Building play'],
      kindergarten: ['Construction play', 'Patterns', 'Measurement basics'],
      early_childhood: ['Shapes & structures', 'Building blocks', 'Simple machines'],
      foundation: ['Construction play', 'Patterns', 'Measurement basics'],
      elementary: ['Simple machines', 'Forces & motion', 'Design challenges'],
      middle_school: ['Engineering design process', 'Algebra', 'Physics basics', 'CAD intro'],
      high_school: ['Calculus', 'Physics', 'Engineering principles', 'Design projects'],
      college_prep: ['AP Calculus', 'AP Physics', 'Engineering fundamentals'],
      undergraduate: ['Structural engineering', 'Thermodynamics', 'Materials science'],
      graduate: ['Advanced engineering', 'Research', 'Innovation systems'],
      continuing_ed: ['Professional development', 'New technologies', 'Certification renewal'],
    },
  },
  {
    id: 'ai_tech',
    label: 'AI & Technology',
    emoji: '🤖',
    coreSubjects: ['technology_ai', 'computer_science', 'mathematics', 'data_science', 'cybersecurity'],
    supportSubjects: ['english_literacy', 'economics_entrepreneurship'],
    description: 'From digital literacy to machine learning, AI development, and tech entrepreneurship.',
    certifications: ['AWS Certified', 'Google Cloud', 'CompTIA A+', 'Python Institute', 'TensorFlow Developer'],
    salaryRange: '$80,000–$300,000+',
    levelProgression: {
      infant: ['Screen time basics', 'Digital awareness'],
      toddler: ['Simple apps', 'Touch interaction'],
      preschool: ['Educational games', 'Basic coding concepts'],
      kindergarten: ['Scratch Jr', 'Computational thinking'],
      early_childhood: ['Digital literacy', 'Educational apps'],
      foundation: ['Scratch basics', 'Computational thinking'],
      elementary: ['Block coding', 'Algorithms', 'Digital citizenship'],
      middle_school: ['Python basics', 'Web development', 'AI concepts'],
      high_school: ['Advanced Python', 'Machine learning intro', 'App development'],
      college_prep: ['AP Computer Science', 'Data structures', 'Algorithms'],
      undergraduate: ['Software engineering', 'AI/ML', 'Cloud computing'],
      graduate: ['Deep learning', 'Research', 'AI ethics', 'Specialization'],
      continuing_ed: ['Upskilling', 'New frameworks', 'Certification prep'],
    },
  },
  {
    id: 'islamic_scholarship',
    label: 'Islamic Scholarship',
    emoji: '📖',
    coreSubjects: ['quran', 'arabic', 'islamic_studies', 'hadith', 'fiqh'],
    supportSubjects: ['english_literacy', 'social_studies', 'academic_writing'],
    description: 'From Quran memorization to advanced Islamic jurisprudence, scholarship, and community leadership.',
    certifications: ['Ijazah in Quran', 'Alimiyyah Degree', 'Islamic Finance Certification'],
    salaryRange: 'Community service focused',
    levelProgression: {
      infant: ['Quran listening', 'Islamic lullabies'],
      toddler: ['Short surahs', 'Islamic stories'],
      preschool: ['Surah Al-Fatiha', 'Basic duas', 'Islamic manners'],
      kindergarten: ['Juz Amma basics', 'Arabic letters', 'Pillars of Islam'],
      early_childhood: ['Quran basics', 'Arabic alphabet', 'Islamic stories'],
      foundation: ['Juz Amma', 'Arabic phonics', 'Pillars of Islam & Iman'],
      elementary: ['Tajweed basics', 'Seerah', 'Fiqh of worship'],
      middle_school: ['Quran memorization', 'Arabic grammar', 'Hadith studies'],
      high_school: ['Advanced Tajweed', 'Usul al-Fiqh intro', 'Islamic history'],
      college_prep: ['Quran sciences', 'Arabic literature', 'Comparative religion'],
      undergraduate: ['Islamic jurisprudence', 'Hadith sciences', 'Arabic linguistics'],
      graduate: ['Advanced Fiqh', 'Research methodology', 'Contemporary issues'],
      continuing_ed: ['Ongoing learning', 'Community education', 'Scholarly updates'],
    },
  },
  {
    id: 'healthcare',
    label: 'Healthcare & Allied Health',
    emoji: '🏥',
    coreSubjects: ['nursing', 'biology', 'chemistry', 'public_health', 'nutrition', 'pharmacy_tech'],
    supportSubjects: ['english_literacy', 'mathematics', 'health_wellness'],
    description: 'Nursing, pharmacy technology, allied health, and healthcare administration pathways.',
    certifications: ['NCLEX-RN', 'CNA', 'Pharmacy Tech Certification', 'Medical Assistant', 'CPR/BLS'],
    salaryRange: '$35,000–$120,000+',
    levelProgression: {
      infant: [], toddler: [], preschool: [], kindergarten: [],
      early_childhood: ['Health awareness'],
      foundation: ['Body systems basics'],
      elementary: ['Health science', 'First aid basics'],
      middle_school: ['Anatomy', 'Health careers exploration'],
      high_school: ['CNA prep', 'Health science', 'Medical terminology'],
      college_prep: ['Pre-nursing', 'Biology', 'Chemistry'],
      undergraduate: ['Nursing program', 'Allied health', 'Clinical rotations'],
      graduate: ['Advanced practice', 'Specialization', 'Healthcare leadership'],
      continuing_ed: ['CEU credits', 'Certification renewal', 'Specialization'],
    },
  },
  {
    id: 'trades_vocational',
    label: 'Trades & Vocational',
    emoji: '🔧',
    coreSubjects: ['carpentry', 'electrical', 'culinary_arts', 'fashion_design', 'agriculture'],
    supportSubjects: ['mathematics', 'english_literacy', 'economics_entrepreneurship'],
    description: 'Skilled trades, vocational training, and entrepreneurship in practical career fields.',
    certifications: ['NCCER', 'Red Seal', 'City & Guilds', 'ServSafe', 'OSHA 10/30'],
    salaryRange: '$40,000–$100,000+',
    levelProgression: {
      infant: [], toddler: [], preschool: [], kindergarten: [],
      early_childhood: ['Hands-on play'],
      foundation: ['Basic tools', 'Safety awareness'],
      elementary: ['Simple projects', 'Measurement'],
      middle_school: ['Trade exploration', 'Basic skills'],
      high_school: ['Trade specialization', 'Apprenticeship prep'],
      college_prep: ['Technical certification', 'Trade school prep'],
      undergraduate: ['Advanced trade skills', 'Business management'],
      graduate: ['Master tradesperson', 'Business ownership'],
      continuing_ed: ['License renewal', 'Advanced techniques', 'Business skills'],
    },
  },
  {
    id: 'finance',
    label: 'Finance & Accounting',
    emoji: '📊',
    coreSubjects: ['accounting_finance', 'economics_entrepreneurship', 'mathematics', 'islamic_finance'],
    supportSubjects: ['english_literacy', 'data_science', 'law'],
    description: 'From personal finance to corporate accounting, investment banking, and Islamic finance.',
    certifications: ['CPA', 'CFA', 'CFP', 'ACCA', 'Islamic Finance Qualification'],
    salaryRange: '$55,000–$200,000+',
    levelProgression: {
      infant: [], toddler: [], preschool: [], kindergarten: [],
      early_childhood: ['Counting money'],
      foundation: ['Saving & spending'],
      elementary: ['Personal finance basics', 'Budgeting'],
      middle_school: ['Economics intro', 'Banking basics'],
      high_school: ['Accounting', 'Investment basics', 'Islamic finance'],
      college_prep: ['AP Economics', 'Financial literacy'],
      undergraduate: ['Accounting', 'Finance', 'Economics'],
      graduate: ['Advanced finance', 'Investment analysis', 'Research'],
      continuing_ed: ['CPA prep', 'CFA prep', 'Professional development'],
    },
  },
  {
    id: 'data_science',
    label: 'Data Science & Analytics',
    emoji: '📈',
    coreSubjects: ['data_science', 'mathematics', 'computer_science', 'technology_ai'],
    supportSubjects: ['english_literacy', 'economics_entrepreneurship'],
    description: 'Statistics, machine learning, data visualization, and AI-driven analytics.',
    certifications: ['Google Data Analytics', 'IBM Data Science', 'AWS Machine Learning'],
    salaryRange: '$85,000–$200,000+',
    levelProgression: {
      infant: [], toddler: [], preschool: [], kindergarten: [],
      early_childhood: ['Pattern recognition'],
      foundation: ['Sorting & classifying'],
      elementary: ['Basic statistics', 'Graphs'],
      middle_school: ['Statistics', 'Spreadsheets'],
      high_school: ['Statistics', 'Python basics', 'Data visualization'],
      college_prep: ['AP Statistics', 'Programming'],
      undergraduate: ['Data science', 'Machine learning', 'Statistics'],
      graduate: ['Advanced ML', 'Research', 'AI specialization'],
      continuing_ed: ['Certification prep', 'New tools', 'Industry applications'],
    },
  },
  {
    id: 'entrepreneurship',
    label: 'Entrepreneurship & Business',
    emoji: '🚀',
    coreSubjects: ['economics_entrepreneurship', 'accounting_finance', 'technology_ai', 'art_design'],
    supportSubjects: ['english_literacy', 'social_studies', 'islamic_finance'],
    description: 'From business ideation to startup launch, scaling, and global market entry.',
    certifications: ['Google Digital Marketing', 'HubSpot', 'Lean Startup Certification'],
    salaryRange: 'Variable — unlimited potential',
    levelProgression: {
      infant: [], toddler: [], preschool: [], kindergarten: [],
      early_childhood: ['Sharing & trading'],
      foundation: ['Simple business concepts'],
      elementary: ['Mini business projects', 'Saving money'],
      middle_school: ['Business plan basics', 'Marketing intro'],
      high_school: ['Business planning', 'Digital marketing', 'Islamic business ethics'],
      college_prep: ['Entrepreneurship', 'Business fundamentals'],
      undergraduate: ['Business administration', 'Startup methodology'],
      graduate: ['Advanced business strategy', 'Investment', 'Global markets'],
      continuing_ed: ['Business scaling', 'New markets', 'Leadership'],
    },
  },
  {
    id: 'education',
    label: 'Education & Teaching',
    emoji: '🎓',
    coreSubjects: ['english_literacy', 'mathematics', 'social_studies', 'technology_ai'],
    supportSubjects: ['arabic', 'islamic_studies', 'academic_writing'],
    description: 'Teacher training, curriculum development, educational technology, and Islamic pedagogy.',
    certifications: ['Teaching License', 'TESOL/TEFL', 'Montessori Certification', 'Islamic Education Diploma'],
    salaryRange: '$40,000–$100,000+',
    levelProgression: {
      infant: [], toddler: [], preschool: [], kindergarten: [],
      early_childhood: ['Learning through play'],
      foundation: ['Peer teaching'],
      elementary: ['Tutoring basics', 'Presentation skills'],
      middle_school: ['Leadership', 'Communication'],
      high_school: ['Education career exploration', 'Tutoring'],
      college_prep: ['Education fundamentals'],
      undergraduate: ['Teacher education', 'Curriculum design'],
      graduate: ['Educational leadership', 'Research', 'Policy'],
      continuing_ed: ['Professional development', 'New methodologies'],
    },
  },
  {
    id: 'global_dev',
    label: 'Global Development',
    emoji: '🌍',
    coreSubjects: ['social_studies', 'english_literacy', 'public_health', 'economics_entrepreneurship'],
    supportSubjects: ['languages', 'arabic', 'agriculture'],
    description: 'International development, humanitarian work, policy, and global citizenship.',
    certifications: ['Project Management Professional', 'USAID Certifications', 'UN Volunteer'],
    salaryRange: '$45,000–$120,000+',
    levelProgression: {
      infant: [], toddler: [], preschool: [], kindergarten: [],
      early_childhood: ['Community helpers'],
      foundation: ['Helping others'],
      elementary: ['Global citizenship', 'Community service'],
      middle_school: ['World issues', 'Development goals'],
      high_school: ['SDGs', 'Policy analysis', 'Global economics'],
      college_prep: ['International relations', 'Development economics'],
      undergraduate: ['International development', 'Policy', 'Research'],
      graduate: ['Advanced policy', 'Research', 'Leadership'],
      continuing_ed: ['Field expertise', 'Policy updates'],
    },
  },
  {
    id: 'law',
    label: 'Law & Public Service',
    emoji: '⚖️',
    coreSubjects: ['english_literacy', 'social_studies', 'academic_writing', 'fiqh'],
    supportSubjects: ['economics_entrepreneurship', 'arabic', 'islamic_studies'],
    description: 'Legal studies, constitutional law, Islamic law, advocacy, and public service.',
    certifications: ['Bar Exam', 'Paralegal Certification', 'Notary Public'],
    salaryRange: '$60,000–$250,000+',
    levelProgression: {
      infant: [], toddler: [], preschool: [], kindergarten: [],
      early_childhood: ['Rules & fairness'],
      foundation: ['Community rules'],
      elementary: ['Civics basics', 'Rights & responsibilities'],
      middle_school: ['Government', 'Legal concepts'],
      high_school: ['Constitutional law', 'Mock trial', 'Islamic law intro'],
      college_prep: ['Pre-law', 'Political science'],
      undergraduate: ['Law school prep', 'Legal studies'],
      graduate: ['Law degree', 'Specialization', 'Bar prep'],
      continuing_ed: ['CLE credits', 'Specialization updates'],
    },
  },
  {
    id: 'arts_design',
    label: 'Arts, Design & Media',
    emoji: '🎨',
    coreSubjects: ['art_design', 'graphic_design', 'video_production', 'technology_ai'],
    supportSubjects: ['english_literacy', 'economics_entrepreneurship'],
    description: 'Visual arts, digital design, media production, and creative entrepreneurship.',
    certifications: ['Adobe Certified', 'Google UX Design', 'Canva Certified'],
    salaryRange: '$40,000–$150,000+',
    levelProgression: {
      infant: [], toddler: [], preschool: [], kindergarten: [],
      early_childhood: ['Drawing & coloring'],
      foundation: ['Art basics', 'Creativity'],
      elementary: ['Visual arts', 'Digital tools'],
      middle_school: ['Design principles', 'Digital art'],
      high_school: ['Portfolio development', 'Graphic design', 'Video production'],
      college_prep: ['Art portfolio', 'Design fundamentals'],
      undergraduate: ['Fine arts', 'Design degree', 'Media production'],
      graduate: ['Advanced design', 'Research', 'Creative direction'],
      continuing_ed: ['New tools', 'Portfolio updates', 'Freelancing'],
    },
  },
  {
    id: 'public_health',
    label: 'Public Health',
    emoji: '🌡️',
    coreSubjects: ['public_health', 'biology', 'chemistry', 'nutrition'],
    supportSubjects: ['mathematics', 'social_studies', 'data_science'],
    description: 'Epidemiology, disease prevention, global health policy, and community health programs.',
    certifications: ['MPH', 'CPH', 'CHES', 'WHO Training Certificates'],
    salaryRange: '$50,000–$130,000+',
    levelProgression: {
      infant: [], toddler: [], preschool: [], kindergarten: [],
      early_childhood: ['Hygiene basics'],
      foundation: ['Health habits'],
      elementary: ['Community health'],
      middle_school: ['Public health concepts'],
      high_school: ['Epidemiology intro', 'Health policy'],
      college_prep: ['Pre-public health'],
      undergraduate: ['Public health degree'],
      graduate: ['MPH', 'Epidemiology', 'Health policy'],
      continuing_ed: ['CEU credits', 'Specialization'],
    },
  },
  {
    id: 'agriculture',
    label: 'Agriculture & Food Systems',
    emoji: '🌾',
    coreSubjects: ['agriculture', 'biology', 'chemistry', 'economics_entrepreneurship'],
    supportSubjects: ['mathematics', 'technology_ai'],
    description: 'Sustainable farming, food security, agribusiness, and agricultural technology.',
    certifications: ['Certified Crop Advisor', 'Organic Farming Certification', 'AgriTech Certifications'],
    salaryRange: '$40,000–$100,000+',
    levelProgression: {
      infant: [], toddler: [], preschool: [], kindergarten: [],
      early_childhood: ['Plants & seeds'],
      foundation: ['Growing food'],
      elementary: ['Plant science', 'Farming basics'],
      middle_school: ['Crop science', 'Soil science'],
      high_school: ['Agricultural science', 'Agribusiness'],
      college_prep: ['Pre-agriculture'],
      undergraduate: ['Agriculture degree', 'Food science'],
      graduate: ['Agricultural research', 'Policy'],
      continuing_ed: ['New farming techniques', 'Certification'],
    },
  },
  {
    id: 'logistics',
    label: 'Logistics & Supply Chain',
    emoji: '🚚',
    coreSubjects: ['logistics_supply_chain', 'economics_entrepreneurship', 'technology_ai'],
    supportSubjects: ['mathematics', 'english_literacy'],
    description: 'Supply chain management, global trade, transportation, and operations management.',
    certifications: ['APICS CSCP', 'CLTD', 'Six Sigma', 'PMP'],
    salaryRange: '$50,000–$130,000+',
    levelProgression: {
      infant: [], toddler: [], preschool: [], kindergarten: [],
      early_childhood: ['Sorting & organizing'],
      foundation: ['Delivery concepts'],
      elementary: ['Supply chain basics'],
      middle_school: ['Trade & commerce'],
      high_school: ['Logistics intro', 'Business operations'],
      college_prep: ['Business fundamentals'],
      undergraduate: ['Supply chain management'],
      graduate: ['Advanced operations', 'Global trade'],
      continuing_ed: ['Certification prep', 'New technologies'],
    },
  },
  {
    id: 'cybersecurity',
    label: 'Cybersecurity',
    emoji: '🔐',
    coreSubjects: ['cybersecurity', 'computer_science', 'technology_ai', 'mathematics'],
    supportSubjects: ['english_literacy', 'data_science'],
    description: 'Network security, ethical hacking, digital forensics, and security operations.',
    certifications: ['CompTIA Security+', 'CEH', 'CISSP', 'CISM'],
    salaryRange: '$75,000–$200,000+',
    levelProgression: {
      infant: [], toddler: [], preschool: [], kindergarten: [],
      early_childhood: ['Online safety basics'],
      foundation: ['Digital safety'],
      elementary: ['Internet safety', 'Password basics'],
      middle_school: ['Cybersecurity awareness', 'Basic networking'],
      high_school: ['Security fundamentals', 'Ethical hacking intro'],
      college_prep: ['CompTIA A+', 'Networking'],
      undergraduate: ['Cybersecurity degree', 'Security operations'],
      graduate: ['Advanced security', 'Research', 'Policy'],
      continuing_ed: ['Certification renewal', 'New threats', 'Specialization'],
    },
  },
];

export const SAMPLE_LESSONS: Lesson[] = [
  {
    id: 'math-elem-001',
    title: 'Multiplication Mastery: Times Tables 1–12',
    titleAr: 'إتقان الضرب: جداول الضرب من ١ إلى ١٢',
    subjectId: 'mathematics',
    level: 'elementary',
    careerRelevance: ['medicine', 'engineering', 'finance', 'ai_tech'],
    duration: 35,
    xpReward: 120,
    videoType: 'animated',
    offlineAvailable: true,
    objectives: [
      { id: 'obj-1', description: 'Recall multiplication facts for tables 1–12', bloomsLevel: 'remember', standard: 'CCSS.MATH.3.OA.C.7' },
      { id: 'obj-2', description: 'Apply multiplication to solve word problems', bloomsLevel: 'apply', standard: 'CCSS.MATH.3.OA.A.3' },
    ],
    activities: ['🎮 Times table race game', '🎵 Multiplication song with rhythm', '📊 Array model builder', '✏️ Word problem worksheet'],
    quiz: [
      { id: 'q1', question: 'What is 7 × 8?', options: ['54', '56', '63', '48'], correct: 1, explanation: '7 × 8 = 56. Think of it as 7 groups of 8 objects.' },
      { id: 'q2', question: 'A classroom has 6 rows with 9 desks each. How many desks total?', options: ['54', '45', '63', '36'], correct: 0, explanation: '6 × 9 = 54 desks total.' },
    ],
    standards: ['CCSS.MATH.3.OA.C.7', 'UK KS2 Maths', 'NERDC Grade 4 Mathematics'],
    accessibilityNotes: ['Audio narration available', 'High contrast mode', 'Screen reader compatible'],
    teacherGuide: 'Use manipulatives and arrays to build conceptual understanding before drilling facts.',
    adabConnection: 'Learning mathematics is a form of worship — Allah created the universe with perfect mathematical order.',
    languages: ['en', 'ar', 'fr', 'ha', 'sw'],
  },
  {
    id: 'quran-found-001',
    title: 'Surah Al-Fatiha: Complete Memorization & Meaning',
    titleAr: 'سورة الفاتحة: الحفظ الكامل والمعنى',
    subjectId: 'quran',
    level: 'foundation',
    careerRelevance: ['islamic_scholarship', 'education'],
    duration: 30,
    xpReward: 150,
    videoType: 'interactive',
    offlineAvailable: true,
    objectives: [
      { id: 'obj-1', description: 'Memorize Surah Al-Fatiha with correct Tajweed', bloomsLevel: 'remember' },
      { id: 'obj-2', description: 'Understand the meaning of each verse', bloomsLevel: 'understand' },
    ],
    activities: ['🎵 Listen and repeat with Sheikh', '📱 Ayah-by-ayah memorization tracker', '🌟 Meaning matching game', '✏️ Tracing Arabic letters'],
    quiz: [
      { id: 'q1', question: 'How many verses does Surah Al-Fatiha have?', options: ['5', '6', '7', '8'], correct: 2, explanation: 'Surah Al-Fatiha has 7 verses (ayat).' },
      { id: 'q2', question: 'What does "Al-Hamdulillah" mean?', options: ['In the name of Allah', 'All praise is for Allah', 'Guide us', 'The Most Merciful'], correct: 1, explanation: 'Al-Hamdulillah means "All praise and thanks are for Allah."' },
    ],
    standards: ['UAE Islamic Studies KG', 'Saudi MOE Islamic Curriculum', 'Nigerian Islamic Studies Foundation'],
    accessibilityNotes: ['Audio recitation by certified Qari', 'Visual Tajweed color coding', 'Parent guide included'],
    teacherGuide: 'Begin with listening, then repeat, then memorize verse by verse. Use the color-coded Tajweed system.',
    adabConnection: 'Al-Fatiha is the greatest surah in the Quran. Memorizing it is the foundation of every Muslim\'s prayer.',
    languages: ['ar', 'en', 'fr', 'ha', 'ur', 'sw'],
  },
  {
    id: 'science-mid-001',
    title: 'Cell Biology: Structure & Function',
    titleAr: 'علم الخلية: البنية والوظيفة',
    subjectId: 'biology',
    level: 'middle_school',
    careerRelevance: ['medicine', 'public_health', 'education'],
    duration: 45,
    xpReward: 180,
    videoType: 'whiteboard',
    offlineAvailable: true,
    objectives: [
      { id: 'obj-1', description: 'Identify organelles in plant and animal cells', bloomsLevel: 'remember', standard: 'NGSS MS-LS1-1' },
      { id: 'obj-2', description: 'Compare and contrast plant and animal cells', bloomsLevel: 'analyze', standard: 'NGSS MS-LS1-2' },
    ],
    activities: ['🔬 Virtual microscope lab', '🎨 Cell diagram labeling', '📊 Venn diagram: plant vs animal cells', '🧫 3D cell model project'],
    quiz: [
      { id: 'q1', question: 'Which organelle is known as the "powerhouse of the cell"?', options: ['Nucleus', 'Ribosome', 'Mitochondria', 'Vacuole'], correct: 2, explanation: 'The mitochondria produces ATP energy for the cell through cellular respiration.' },
      { id: 'q2', question: 'Which structure is found in plant cells but NOT animal cells?', options: ['Cell membrane', 'Nucleus', 'Cell wall', 'Mitochondria'], correct: 2, explanation: 'Plant cells have a rigid cell wall made of cellulose; animal cells do not.' },
    ],
    standards: ['NGSS MS-LS1', 'UK KS3 Biology', 'CAPS Grade 8 Life Sciences', 'NERDC JSS3 Biology'],
    accessibilityNotes: ['3D interactive cell model', 'Audio descriptions', 'Simplified vocabulary mode'],
    teacherGuide: 'Use the virtual microscope before the lesson. Have students draw their own cell diagrams.',
    projectTask: 'Build a 3D model of a plant or animal cell using household materials.',
    adabConnection: 'The complexity of a single cell is a sign of Allah\'s infinite wisdom and creative power.',
    languages: ['en', 'ar', 'fr', 'sw'],
  },
  {
    id: 'arabic-elem-001',
    title: 'Arabic Alphabet: Letters Alif to Jeem',
    titleAr: 'الحروف العربية: من الألف إلى الجيم',
    subjectId: 'arabic',
    level: 'elementary',
    careerRelevance: ['islamic_scholarship', 'education', 'global_dev'],
    duration: 25,
    xpReward: 100,
    videoType: 'cartoon',
    offlineAvailable: true,
    objectives: [
      { id: 'obj-1', description: 'Recognize and write Arabic letters Alif, Ba, Ta, Tha, Jeem', bloomsLevel: 'remember' },
      { id: 'obj-2', description: 'Pronounce each letter with correct Makhraj', bloomsLevel: 'apply' },
    ],
    activities: ['✏️ Letter tracing worksheets', '🎵 Arabic alphabet song', '🎮 Letter matching game', '📱 Pronunciation practice with AI feedback'],
    quiz: [
      { id: 'q1', question: 'Which letter comes after Alif (ا)?', options: ['ج', 'ب', 'ت', 'ث'], correct: 1, explanation: 'Ba (ب) comes after Alif (ا) in the Arabic alphabet.' },
      { id: 'q2', question: 'How many dots does the letter Ba (ب) have?', options: ['0', '1', '2', '3'], correct: 1, explanation: 'Ba (ب) has one dot below the letter.' },
    ],
    standards: ['UAE Arabic Curriculum Grade 1', 'Saudi MOE Arabic Grade 1', 'Nigerian Islamic Studies'],
    accessibilityNotes: ['Audio pronunciation by native speaker', 'Tracing guides for motor skills', 'Parent guide'],
    teacherGuide: 'Use the letter cards and have students trace in sand or on whiteboards before paper.',
    adabConnection: 'Learning Arabic is the key to understanding the Quran directly. Every letter is a step closer to Allah\'s words.',
    languages: ['ar', 'en', 'fr', 'ha', 'ur'],
  },
  {
    id: 'nursing-ug-001',
    title: 'Fundamentals of Patient Assessment',
    titleAr: 'أساسيات تقييم المريض',
    subjectId: 'nursing',
    level: 'undergraduate',
    careerRelevance: ['healthcare', 'medicine', 'public_health'],
    duration: 60,
    xpReward: 250,
    videoType: 'ai_narrator',
    offlineAvailable: false,
    objectives: [
      { id: 'obj-1', description: 'Perform a systematic head-to-toe physical assessment', bloomsLevel: 'apply' },
      { id: 'obj-2', description: 'Document assessment findings using SBAR format', bloomsLevel: 'create' },
    ],
    activities: ['🏥 Virtual patient simulation', '📋 SBAR documentation practice', '🩺 Vital signs measurement lab', '📊 Case study analysis'],
    quiz: [
      { id: 'q1', question: 'What does SBAR stand for?', options: ['Situation, Background, Assessment, Recommendation', 'Status, Baseline, Action, Review', 'Symptoms, Background, Assessment, Response', 'Situation, Brief, Action, Report'], correct: 0, explanation: 'SBAR is a communication framework: Situation, Background, Assessment, Recommendation.' },
      { id: 'q2', question: 'Normal adult resting heart rate is:', options: ['40–60 bpm', '60–100 bpm', '100–120 bpm', '120–140 bpm'], correct: 1, explanation: 'Normal adult resting heart rate is 60–100 beats per minute.' },
    ],
    standards: ['NCLEX-RN Standards', 'NMC UK Standards', 'NMCN Nigeria Standards'],
    accessibilityNotes: ['Closed captions', 'Screen reader compatible', 'Downloadable reference cards'],
    teacherGuide: 'Use simulation mannequins alongside this lesson. Pair students for peer assessment practice.',
    languages: ['en', 'ar', 'fr'],
  },
  {
    id: 'coding-mid-001',
    title: 'Python Basics: Variables & Data Types',
    titleAr: 'أساسيات بايثون: المتغيرات وأنواع البيانات',
    subjectId: 'computer_science',
    level: 'middle_school',
    careerRelevance: ['ai_tech', 'data_science', 'engineering', 'finance'],
    duration: 50,
    xpReward: 200,
    videoType: 'interactive',
    offlineAvailable: true,
    objectives: [
      { id: 'obj-1', description: 'Declare variables and assign values in Python', bloomsLevel: 'apply' },
      { id: 'obj-2', description: 'Identify and use string, integer, float, and boolean data types', bloomsLevel: 'understand' },
    ],
    activities: ['💻 Live code editor in browser', '🎮 Variable assignment game', '🐛 Debug the code challenge', '📝 Mini project: personal profile program'],
    quiz: [
      { id: 'q1', question: 'What is the output of: print(type(42))?', options: ["<class 'str'>", "<class 'int'>", "<class 'float'>", "<class 'bool'>"], correct: 1, explanation: '42 is an integer, so type(42) returns <class \'int\'>.' },
      { id: 'q2', question: 'Which is a valid Python variable name?', options: ['2name', 'my-name', 'my_name', 'my name'], correct: 2, explanation: 'Variable names cannot start with numbers, contain hyphens, or have spaces. my_name is valid.' },
    ],
    standards: ['CSTA K-12 CS Standards', 'UK Computing KS3', 'Australian Digital Technologies'],
    accessibilityNotes: ['In-browser code editor', 'Syntax highlighting', 'Error explanations in plain language'],
    teacherGuide: 'Use the live code editor. Have students modify the examples before writing their own.',
    adabConnection: 'Coding is a form of problem-solving — a skill that can be used to serve humanity and the ummah.',
    languages: ['en', 'ar', 'fr'],
  },
  {
    id: 'econ-hs-001',
    title: 'Islamic Finance: Halal vs Haram Transactions',
    titleAr: 'التمويل الإسلامي: المعاملات الحلال والحرام',
    subjectId: 'islamic_finance',
    level: 'high_school',
    careerRelevance: ['finance', 'islamic_scholarship', 'entrepreneurship', 'law'],
    duration: 45,
    xpReward: 180,
    videoType: 'whiteboard',
    offlineAvailable: true,
    objectives: [
      { id: 'obj-1', description: 'Distinguish between Riba (interest) and halal profit', bloomsLevel: 'analyze' },
      { id: 'obj-2', description: 'Explain Murabaha, Musharakah, and Ijarah contracts', bloomsLevel: 'understand' },
    ],
    activities: ['📊 Case study: Islamic bank vs conventional bank', '🎭 Role play: halal business negotiation', '📝 Contract analysis worksheet', '💡 Design a halal business model'],
    quiz: [
      { id: 'q1', question: 'What is Riba?', options: ['Halal profit from trade', 'Interest or usury prohibited in Islam', 'A type of Islamic contract', 'Charitable giving'], correct: 1, explanation: 'Riba refers to interest or usury, which is strictly prohibited in Islam.' },
      { id: 'q2', question: 'In a Musharakah contract, profits are shared:', options: ['Only by the investor', 'Only by the bank', 'According to agreed ratios between partners', 'Equally regardless of contribution'], correct: 2, explanation: 'Musharakah is a partnership where profits and losses are shared according to agreed ratios.' },
    ],
    standards: ['AAOIFI Standards', 'UAE Islamic Finance Curriculum', 'INCEIF Standards'],
    accessibilityNotes: ['Audio explanations', 'Visual contract diagrams', 'Glossary of Islamic finance terms'],
    teacherGuide: 'Use real-world examples from Islamic banks in UAE, Malaysia, and UK. Compare with conventional banking.',
    adabConnection: 'Halal earnings are a form of worship. The Prophet ﷺ said: "The best earning is that of a man who works with his hands."',
    languages: ['en', 'ar', 'fr', 'ur'],
  },
  {
    id: 'carpentry-hs-001',
    title: 'Carpentry Fundamentals: Wood Joints & Safety',
    titleAr: 'أساسيات النجارة: وصلات الخشب والسلامة',
    subjectId: 'carpentry',
    level: 'high_school',
    careerRelevance: ['trades_vocational', 'engineering'],
    duration: 55,
    xpReward: 160,
    videoType: 'ai_narrator',
    offlineAvailable: true,
    objectives: [
      { id: 'obj-1', description: 'Identify and demonstrate 5 common wood joints', bloomsLevel: 'apply' },
      { id: 'obj-2', description: 'Apply workshop safety protocols correctly', bloomsLevel: 'apply' },
    ],
    activities: ['🪚 Virtual workshop simulation', '📐 Blueprint reading exercise', '🔨 Joint identification quiz', '📋 Safety checklist completion'],
    quiz: [
      { id: 'q1', question: 'Which wood joint is strongest for corner connections?', options: ['Butt joint', 'Dovetail joint', 'Lap joint', 'Miter joint'], correct: 1, explanation: 'The dovetail joint is one of the strongest corner joints due to its interlocking design.' },
      { id: 'q2', question: 'Before using power tools, you should always:', options: ['Check your phone', 'Wear appropriate PPE', 'Work alone', 'Skip the safety briefing'], correct: 1, explanation: 'Personal Protective Equipment (PPE) including safety glasses and hearing protection must always be worn.' },
    ],
    standards: ['NCCER Carpentry Level 1', 'City & Guilds 6217', 'OSHA 10 Standards'],
    accessibilityNotes: ['Step-by-step visual guides', 'Safety warnings highlighted', 'Video demonstrations'],
    teacherGuide: 'Demonstrate each joint type before students practice. Enforce PPE rules strictly.',
    languages: ['en', 'ar', 'fr', 'ha'],
  },
];

// Helper functions
export function getSubjectsByCategory(category: SubjectCategory): Subject[] {
  return SUBJECTS.filter(s => s.category === category);
}

export function getLessonsBySubject(subjectId: string): Lesson[] {
  return SAMPLE_LESSONS.filter(l => l.subjectId === subjectId);
}

export function getLessonsByLevel(level: DevelopmentalLevel): Lesson[] {
  return SAMPLE_LESSONS.filter(l => l.level === level);
}

export function getPathwaySubjects(pathwayId: CareerTrack): Subject[] {
  const pathway = CAREER_PATHWAYS.find(p => p.id === pathwayId);
  if (!pathway) return [];
  const allSubjectIds = [...pathway.coreSubjects, ...pathway.supportSubjects];
  return SUBJECTS.filter(s => allSubjectIds.includes(s.id));
}

export function getFeaturedSubjects(): Subject[] {
  return SUBJECTS.filter(s => s.featured);
}
